import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator,
  FileText,
  TrendingUp,
  PlusCircle,
  Download,
  CreditCard,
  ArrowRight,
  ShieldCheck,
  CheckCircle,
  ExternalLink,
  ChevronRight,
  Printer,
  Calendar,
  AlertCircle,
  Lock,
  Mail,
  User as UserIcon,
  LogOut,
  RefreshCw,
  Clock,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useGame } from "@/context/GameContext";

export default function AdvertiserPortal() {
  const { parentId, email, parentToken, update, logoutParent } = useGame();
  const [activeTab, setActiveTab] = useState("create"); // create | my-campaigns
  const [activeStep, setActiveStep] = useState("calculate"); // calculate | submit | dashboard | invoice
  
  // Package Calculator State
  const [selectedPkg, setSelectedPkg] = useState("fixed"); // fixed | cpm | cpc
  const [durationMonths, setDurationMonths] = useState(1);
  const [targetImpressions, setTargetImpressions] = useState(50000);
  const [targetClicks, setTargetClicks] = useState(1000);
  const [calculatedCost, setCalculatedCost] = useState(2500000);

  // Form Fields
  const [brandName, setBrandName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Campaign State (Simulated active campaign)
  const [campaign, setCampaign] = useState(null);
  const [simulatedStats, setSimulatedStats] = useState({
    impressions: 0,
    clicks: 0,
    ctr: 0.0,
    currentCost: 0
  });

  // Authentication State
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // login | register
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Real Database Campaigns
  const [realCampaigns, setRealCampaigns] = useState([]);
  const [selectedRealCampaignId, setSelectedRealCampaignId] = useState("");
  const [fetchingCampaigns, setFetchingCampaigns] = useState(false);

  // Calculate costs in real-time
  useEffect(() => {
    if (selectedPkg === "fixed") {
      setCalculatedCost(durationMonths * 2500000);
    } else if (selectedPkg === "cpm") {
      setCalculatedCost((targetImpressions / 1000) * 50000);
    } else if (selectedPkg === "cpc") {
      setCalculatedCost(targetClicks * 2000);
    }
  }, [selectedPkg, durationMonths, targetImpressions, targetClicks]);

  // Set default dates when moving to submit step
  const handleProceedToSubmit = () => {
    const today = new Date().toISOString().substring(0, 10);
    const in30Days = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10);
    setStartDate(today);
    setEndDate(in30Days);
    setActiveStep("submit");
  };

  // Fetch real campaigns from DB
  const fetchRealCampaigns = useCallback(async () => {
    if (!parentId) return;
    setFetchingCampaigns(true);
    try {
      const data = await api.getParentAds(parentId);
      setRealCampaigns(data || []);
    } catch (err) {
      console.error("Gagal mengambil daftar iklan riil:", err);
    } finally {
      setFetchingCampaigns(false);
    }
  }, [parentId]);

  useEffect(() => {
    if (parentId) {
      fetchRealCampaigns();
    } else {
      setRealCampaigns([]);
      setSelectedRealCampaignId("");
    }
  }, [parentId, fetchRealCampaigns]);

  // Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) return;
    setLoading(true);
    try {
      const data = await api.loginParent({ email: loginEmail, password: loginPassword });
      update({
        parentId: data.id,
        email: data.email,
        parentToken: data.token,
        language: data.language || "id"
      });
      toast.success("Berhasil masuk ke akun pengiklan! 🚀");
      setShowAuthModal(false);
      setLoginPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email atau password salah.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Register
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerName || !registerEmail || !registerPassword) return;
    setLoading(true);
    try {
      const data = await api.createParent({
        name: registerName,
        email: registerEmail,
        password: registerPassword,
        language: "id"
      });
      update({
        parentId: data.id,
        email: data.email,
        parentToken: data.token,
        language: "id"
      });
      toast.success("Registrasi pengiklan berhasil! Silakan kelola kampanye Anda. 🚀");
      setShowAuthModal(false);
      setRegisterPassword("");
      setRegisterName("");
      setRegisterEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal melakukan registrasi.");
    } finally {
      setLoading(false);
    }
  };

  // Open Midtrans Snap Payment
  const triggerMidtransPayment = (token, redirectUrl, campaignId) => {
    if (window.snap) {
      window.snap.pay(token, {
        onSuccess: async function (result) {
          toast.success("Pembayaran Iklan Berhasil! Menunggu verifikasi admin. 🌟");
          await fetchRealCampaigns();
          setActiveTab("my-campaigns");
          setSelectedRealCampaignId(campaignId);
        },
        onPending: function (result) {
          toast.info("Menunggu pembayaran Anda. Silakan cek invoice.");
          fetchRealCampaigns();
          setActiveTab("my-campaigns");
        },
        onError: function (result) {
          toast.error("Pembayaran gagal!");
        },
        onClose: function () {
          toast.info("Transaksi ditangguhkan.");
          fetchRealCampaigns();
          setActiveTab("my-campaigns");
        }
      });
    } else {
      window.open(redirectUrl, "_blank");
    }
  };

  // Submit ad campaign
  const handleCampaignSubmit = async (e) => {
    e.preventDefault();
    if (!brandName || !imageUrl || !targetUrl) {
      toast.warning("Mohon lengkapi semua kolom materi iklan.");
      return;
    }

    // Check if user is logged in
    if (!parentId) {
      // User is Guest - run simulation
      const mockCampaign = {
        id: "CAM-" + Math.floor(100000 + Math.random() * 900000),
        brandName,
        imageUrl,
        targetUrl,
        startDate,
        endDate,
        packageType: selectedPkg,
        totalBudget: calculatedCost,
        status: "Active (Running)",
        invoiceNo: "INV-2026-" + Math.floor(5000 + Math.random() * 5000)
      };

      setCampaign(mockCampaign);
      toast.success("Kampanye Uji Coba Simulasi Berhasil Terdaftar! 🚀");
      
      setSimulatedStats({
        impressions: 0,
        clicks: 0,
        ctr: 0.0,
        currentCost: selectedPkg === "fixed" ? calculatedCost : 0
      });
      
      setActiveStep("dashboard");
      return;
    }

    // Logged In: Register Real Campaign & Charge Midtrans
    setLoading(true);
    try {
      const payload = {
        parent_id: parentId,
        advertiser_name: brandName,
        image_url: imageUrl,
        target_url: targetUrl,
        placement: "header",
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
        package_type: selectedPkg,
        target_limit: selectedPkg === "fixed" ? 0 : selectedPkg === "cpm" ? targetImpressions : targetClicks
      };

      const res = await api.checkoutAdCampaign(payload);
      if (res && res.token) {
        triggerMidtransPayment(res.token, res.redirect_url, res.campaign_id);
      } else {
        toast.error("Gagal menginisialisasi invoice pembayaran.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mendaftarkan iklan riil.");
    } finally {
      setLoading(false);
    }
  };

  // Simulate real-time dashboard analytics accumulation (Sandbox mode only)
  useEffect(() => {
    if (activeStep !== "dashboard" || !campaign) return;

    const interval = setInterval(() => {
      setSimulatedStats(prev => {
        const addImp = Math.floor(Math.random() * 25) + 5;
        const addClick = Math.random() < 0.15 ? 1 : 0; 
        const newImp = prev.impressions + addImp;
        const newClick = prev.clicks + addClick;
        const newCtr = newImp > 0 ? parseFloat(((newClick / newImp) * 100).toFixed(2)) : 0;
        
        let newCost = prev.currentCost;
        if (selectedPkg === "cpm") {
          newCost = Math.round((newImp / 1000) * 50000);
        } else if (selectedPkg === "cpc") {
          newCost = newClick * 2000;
        }

        return {
          impressions: newImp,
          clicks: newClick,
          ctr: newCtr,
          currentCost: newCost
        };
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [activeStep, campaign, selectedPkg]);

  const formatIDR = (num) => {
    return "Rp " + (num || 0).toLocaleString("id-ID");
  };

  const getPackageLabel = (type) => {
    if (type === "fixed") return "Fixed Rate Bulanan";
    if (type === "cpm") return "CPM (Cost Per Mille)";
    return "CPC (Cost Per Click)";
  };

  // Find selected campaign object
  const selectedRealCampaign = realCampaigns.find(c => c.id === selectedRealCampaignId);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans relative overflow-hidden grain pb-16">
      {/* Background Decorative Blurs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-10 left-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Navbar / Top Bar */}
      <div className="w-full bg-slate-900/40 border-b border-slate-800/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 font-extrabold text-lg">
              B
            </div>
            <div>
              <span className="font-display font-bold text-sm tracking-wide text-slate-200">BabyLabs Partner</span>
              <span className="text-[10px] bg-sky-500/10 border border-sky-500/30 text-sky-400 font-extrabold px-1.5 py-0.5 rounded ml-2 uppercase">Self-Service</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {parentId ? (
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400 font-bold hidden sm:inline">{email}</span>
                <button
                  onClick={() => {
                    logoutParent();
                    toast.success("Berhasil keluar dari akun pengiklan.");
                    setActiveTab("create");
                  }}
                  className="flex items-center gap-1.5 text-xs text-rose-400 hover:text-rose-300 font-bold bg-rose-500/10 border border-rose-500/20 px-3 py-1.5 rounded-xl transition-all"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>Keluar</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setAuthMode("login");
                    setShowAuthModal(true);
                  }}
                  className="text-xs text-slate-300 hover:text-white font-bold px-3 py-1.5 hover:bg-slate-900 rounded-xl transition-all"
                >
                  Masuk Pengiklan
                </button>
                <button
                  onClick={() => {
                    setAuthMode("register");
                    setShowAuthModal(true);
                  }}
                  className="text-xs text-slate-950 font-bold px-3 py-1.5 bg-amber-500 hover:bg-amber-400 rounded-xl transition-all shadow-sm"
                >
                  Daftar
                </button>
              </div>
            )}
            
            <a href="/parent" className="text-xs font-bold px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700/60 rounded-xl transition-all">
              Kembali ke Aplikasi
            </a>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-5xl mx-auto px-6 mt-10">
        
        {/* Toggle Simulation vs Real Campaign Tab (If Logged In) */}
        {parentId && (
          <div className="flex justify-center mb-8">
            <div className="bg-slate-900/60 border border-slate-800 p-1.5 rounded-2xl flex gap-1">
              <button
                onClick={() => {
                  setActiveTab("create");
                  setActiveStep("calculate");
                }}
                className={`px-5 py-2 text-xs font-bold rounded-xl transition-all ${
                  activeTab === "create"
                    ? "bg-amber-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                Buat Iklan Baru
              </button>
              <button
                onClick={() => {
                  setActiveTab("my-campaigns");
                  fetchRealCampaigns();
                }}
                className={`px-5 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${
                  activeTab === "my-campaigns"
                    ? "bg-amber-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                <span>Dasbor Iklan Saya</span>
                {realCampaigns.length > 0 && (
                  <span className={`px-1.5 py-0.5 text-[10px] rounded-full font-extrabold ${activeTab === 'my-campaigns' ? 'bg-slate-950 text-amber-500' : 'bg-amber-500/10 text-amber-400 border border-amber-500/30'}`}>
                    {realCampaigns.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Tab Switch Contents */}
        {activeTab === "create" ? (
          <div>
            {/* Banner Welcome */}
            <div className="text-center mb-10">
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-amber-400 via-amber-200 to-sky-400 bg-clip-text text-transparent">
                {parentId ? "Buat Kampanye Iklan Baru 🚀" : "Portal Kemitraan Mandiri Pengiklan 📣"}
              </h1>
              <p className="text-slate-400 text-sm font-bold mt-2 max-w-2xl mx-auto">
                {parentId 
                  ? "Daftarkan brand Anda, tentukan target performa iklan, dan jalankan pembayaran dengan gerbang pembayaran Midtrans Snap secara instan."
                  : "Daftarkan materi promosi Anda, pilih skema pembayaran, dan kelola invoice serta laporan performa secara otomatis dalam satu sistem terintegrasi."}
              </p>
              {!parentId && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl inline-flex items-center gap-2 max-w-xl mx-auto">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span className="text-xs text-amber-300 font-bold text-left leading-normal">
                    Anda berada dalam <strong>Mode Sandbox (Simulasi)</strong>. Silakan <button onClick={() => { setAuthMode("login"); setShowAuthModal(true); }} className="text-amber-400 underline hover:text-amber-300">Masuk</button> atau <button onClick={() => { setAuthMode("register"); setShowAuthModal(true); }} className="text-amber-400 underline hover:text-amber-300">Daftar</button> untuk mempublikasikan iklan riil yang terverifikasi.
                  </span>
                </div>
              )}
            </div>

            {/* Steps Progress Visualizer */}
            <div className="grid grid-cols-4 gap-4 mb-10 text-center relative max-w-3xl mx-auto">
              {[
                { id: "calculate", label: "1. Kalkulator & Paket" },
                { id: "submit", label: "2. Materi Iklan" },
                { id: "dashboard", label: "3. Laporan Live" },
                { id: "invoice", label: "4. Tagihan & Invoice" }
              ].map((step, idx) => {
                const isActive = activeStep === step.id;
                const isCompleted = 
                  (activeStep === "submit" && idx === 0) ||
                  (activeStep === "dashboard" && idx <= 1) ||
                  (activeStep === "invoice" && idx <= 2);
                
                return (
                  <div key={step.id} className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border transition-all ${
                      isActive 
                        ? "bg-amber-500 border-amber-400 text-slate-950 shadow-lg shadow-amber-500/20"
                        : isCompleted 
                        ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
                        : "bg-slate-900 border-slate-800 text-slate-500"
                    }`}>
                      {isCompleted ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                    </div>
                    <span className={`text-[11px] font-extrabold mt-2 transition-colors ${
                      isActive ? "text-amber-400" : isCompleted ? "text-emerald-500" : "text-slate-500"
                    }`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Content Tabs area */}
            <AnimatePresence mode="wait">
              
              {/* STEP 1: CALCULATOR */}
              {activeStep === "calculate" && (
                <motion.div
                  key="calculate"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                >
                  {/* Packages selection */}
                  <div className="lg:col-span-2 space-y-4">
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl">
                      <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-amber-400" />
                        Pilih Skema Monetisasi Iklan Anda
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {[
                          {
                            id: "fixed",
                            title: "Fixed Rate",
                            desc: "Impresi tak terbatas per bulan",
                            price: "Rp 2,5 Jt / bln",
                            badge: "Paling Populer"
                          },
                          {
                            id: "cpm",
                            title: "CPM Package",
                            desc: "Bayar per 1.000 tayangan banner",
                            price: "Rp 50rb / CPM",
                            badge: "Presisi Tinggi"
                          },
                          {
                            id: "cpc",
                            title: "CPC Package",
                            desc: "Hanya bayar ketika diklik orang tua",
                            price: "Rp 2rb / Klik",
                            badge: "ROI Maksimal"
                          }
                        ].map((pkg) => (
                          <button
                            key={pkg.id}
                            type="button"
                            onClick={() => setSelectedPkg(pkg.id)}
                            className={`p-4 rounded-2xl border text-left transition-all flex flex-col justify-between relative overflow-hidden ${
                              selectedPkg === pkg.id
                                ? "bg-amber-500/5 border-amber-500 shadow-md"
                                : "bg-slate-950/40 border-slate-800 hover:border-slate-700"
                            }`}
                          >
                            {selectedPkg === pkg.id && (
                              <div className="absolute top-0 right-0 bg-amber-500 text-slate-950 text-[8px] font-extrabold px-2 py-0.5 rounded-bl uppercase tracking-wider">
                                {pkg.badge}
                              </div>
                            )}
                            <div>
                              <div className="font-bold text-sm text-slate-200">{pkg.title}</div>
                              <p className="text-[11px] text-slate-500 font-bold mt-1 leading-snug">{pkg.desc}</p>
                            </div>
                            <div className="font-extrabold text-base text-amber-400 mt-4">{pkg.price}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Parameters configuration */}
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl space-y-5">
                      <h3 className="font-display font-bold text-lg">Konfigurasi Target Kampanye</h3>
                      
                      {selectedPkg === "fixed" && (
                        <div>
                          <label htmlFor="duration-months" className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                            Durasi Penayangan (Bulan)
                          </label>
                          <div id="duration-months" className="flex gap-3">
                            {[1, 3, 6, 12].map(m => (
                              <button
                                key={m}
                                onClick={() => setDurationMonths(m)}
                                className={`flex-1 h-10 rounded-xl font-bold text-xs border transition-all ${
                                  durationMonths === m
                                    ? "bg-amber-500 border-amber-400 text-slate-950"
                                    : "bg-slate-950/40 border-slate-800 hover:border-slate-700 text-slate-400"
                                }`}
                              >
                                {m} Bulan {m > 1 ? `(Diskon ${m === 3 ? '5' : m === 6 ? '10' : '15'}%)` : ''}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedPkg === "cpm" && (
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label htmlFor="target-impressions" className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider">
                              Target Jumlah Impresi (Tayangan)
                            </label>
                            <span className="text-xs text-amber-400 font-mono font-bold">
                              {targetImpressions.toLocaleString()} Impresi
                            </span>
                          </div>
                          <input
                            id="target-impressions"
                            type="range"
                            min="10000"
                            max="500000"
                            step="10000"
                            value={targetImpressions}
                            onChange={(e) => setTargetImpressions(parseInt(e.target.value))}
                            className="w-full accent-amber-500"
                          />
                          <div className="flex justify-between text-[10px] text-slate-500 font-bold mt-1">
                            <span>10.000 Impresi</span>
                            <span>250.000 Impresi</span>
                            <span>500.000 Impresi</span>
                          </div>
                        </div>
                      )}

                      {selectedPkg === "cpc" && (
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label htmlFor="target-clicks" className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider">
                              Target Jumlah Klik
                            </label>
                            <span className="text-xs text-amber-400 font-mono font-bold">
                              {targetClicks.toLocaleString()} Klik
                            </span>
                          </div>
                          <input
                            id="target-clicks"
                            type="range"
                            min="500"
                            max="20000"
                            step="500"
                            value={targetClicks}
                            onChange={(e) => setTargetClicks(parseInt(e.target.value))}
                            className="w-full accent-amber-500"
                          />
                          <div className="flex justify-between text-[10px] text-slate-500 font-bold mt-1">
                            <span>500 Klik</span>
                            <span>10.000 Klik</span>
                            <span>20.000 Klik</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Estimate Summary Panel */}
                  <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl flex flex-col justify-between">
                    <div>
                      <h3 className="font-display font-bold text-lg mb-4">Estimasi Biaya Kampanye</h3>
                      <div className="space-y-4">
                        <div className="pb-3 border-b border-slate-800">
                          <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Jenis Paket</span>
                          <div className="font-bold text-sm text-slate-200 mt-0.5">{getPackageLabel(selectedPkg)}</div>
                        </div>

                        <div className="pb-3 border-b border-slate-800">
                          <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Metrik Parameter</span>
                          <div className="font-bold text-sm text-slate-200 mt-0.5">
                            {selectedPkg === "fixed" && `${durationMonths} Bulan Tayang`}
                            {selectedPkg === "cpm" && `${targetImpressions.toLocaleString()} Impresi`}
                            {selectedPkg === "cpc" && `${targetClicks.toLocaleString()} Klik`}
                          </div>
                        </div>

                        <div>
                          <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">Harga Satuan Rate Card</span>
                          <div className="font-bold text-sm text-slate-200 mt-0.5">
                            {selectedPkg === "fixed" && `${formatIDR(2500000)} / Bulan`}
                            {selectedPkg === "cpm" && `${formatIDR(50000)} / 1.000 Impresi`}
                            {selectedPkg === "cpc" && `${formatIDR(2000)} / Klik`}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 pt-4 border-t border-slate-800">
                      <div className="flex justify-between items-baseline mb-4">
                        <span className="text-sm text-slate-400 font-bold">Total Anggaran:</span>
                        <span className="text-2xl font-extrabold text-amber-400">{formatIDR(calculatedCost)}</span>
                      </div>
                      <Button
                        onClick={handleProceedToSubmit}
                        className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-bold text-sm shadow-md shadow-amber-500/10 flex items-center justify-center gap-2"
                      >
                        <span>Lanjutkan ke Materi Iklan</span>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* STEP 2: SUBMIT MATERIAL */}
              {activeStep === "submit" && (
                <motion.div
                  key="submit"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  className="max-w-2xl mx-auto bg-slate-900/60 border border-slate-800/80 rounded-[2rem] p-6 shadow-xl"
                >
                  <h3 className="font-display font-bold text-lg mb-6 flex items-center gap-2">
                    <PlusCircle className="w-5 h-5 text-amber-400" />
                    Lengkapi Detail & Desain Materi Iklan
                  </h3>
                  
                  <form onSubmit={handleCampaignSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="brand-name" className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                        Nama Pengiklan / Brand
                      </label>
                      <input
                        id="brand-name"
                        type="text"
                        value={brandName}
                        onChange={(e) => setBrandName(e.target.value)}
                        className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                        placeholder="e.g. Nutricia Bebelac 3"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="image-url" className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                        URL Gambar Banner Iklan (Rasio 2:1 disarankan)
                      </label>
                      <input
                        id="image-url"
                        type="url"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                        placeholder="e.g. https://images.unsplash.com/photo-1596464716127-f2a82984de30?w=600"
                        required
                      />
                      <div className="flex items-center gap-1 text-[10px] text-slate-500 font-bold mt-1.5">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>Anda bisa menyalin URL contoh foto balita Unsplash di atas untuk simulasi visual.</span>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="target-url" className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                        Tautan Tujuan (Redirect URL)
                      </label>
                      <input
                        id="target-url"
                        type="url"
                        value={targetUrl}
                        onChange={(e) => setTargetUrl(e.target.value)}
                        className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                        placeholder="e.g. https://bebelac.co.id/promo-hebat"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="start-date" className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                          Tanggal Mulai Kampanye
                        </label>
                        <input
                          id="start-date"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="end-date" className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                          Tanggal Selesai Kampanye
                        </label>
                        <input
                          id="end-date"
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full h-11 px-4 rounded-2xl bg-slate-950/40 border border-slate-800 text-slate-100 focus:outline-none focus:border-amber-400 transition-colors font-bold text-sm"
                          required
                        />
                      </div>
                    </div>

                    <div className="pt-4 flex gap-4">
                      <button
                        type="button"
                        onClick={() => setActiveStep("calculate")}
                        className="flex-1 h-11 rounded-2xl border border-slate-700 text-slate-300 font-display text-sm font-bold transition-all hover:bg-slate-800"
                        disabled={loading}
                      >
                        Kembali
                      </button>
                      <Button
                        type="submit"
                        className="flex-1 h-11 rounded-2xl bg-amber-500 hover:bg-amber-400 text-white font-display text-sm font-bold shadow-[0_4px_0_#d97706] hover:shadow-[0_2px_0_#d97706] hover:translate-y-[2px] transition-all"
                        disabled={loading}
                      >
                        {loading ? "Memproses..." : parentId ? "Daftar & Bayar Iklan 💳" : "Jalankan Simulasi Iklan 🚀"}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              )}

              {/* STEP 3: LIVE ANALYTICS REPORT DASHBOARD */}
              {activeStep === "dashboard" && campaign && (
                <motion.div
                  key="dashboard"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  className="space-y-6"
                >
                  {/* Campaign status summary */}
                  <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div>
                      <span className="text-[10px] font-extrabold tracking-wider bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-2 py-0.5 rounded uppercase">
                        Status: {campaign.status}
                      </span>
                      <h3 className="font-display font-bold text-xl text-slate-100 mt-2">
                        Laporan Kampanye: {campaign.brandName}
                      </h3>
                      <p className="text-xs text-slate-500 font-mono font-bold mt-1">
                        ID Kampanye: {campaign.id} | Skema: {getPackageLabel(campaign.packageType)}
                      </p>
                    </div>

                    <div className="flex gap-3 w-full sm:w-auto">
                      <Button
                        onClick={() => setActiveStep("invoice")}
                        className="flex-1 sm:flex-initial h-11 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700/60 text-slate-200 text-xs font-bold flex items-center justify-center gap-2"
                      >
                        <FileText className="w-4 h-4" />
                        <span>Lihat Invoice / Tagihan</span>
                      </Button>
                      <Button
                        onClick={() => {
                          toast.success("Laporan Uji Coba Berhasil Diekspor!");
                          window.print();
                        }}
                        className="flex-1 sm:flex-initial h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold flex items-center justify-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        <span>Unduh Laporan</span>
                      </Button>
                    </div>
                  </div>

                  {/* LIVE ANALYTICS COUNTERS */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[
                      {
                        title: "Impresi Real-Time",
                        value: simulatedStats.impressions.toLocaleString(),
                        unit: "Tayangan",
                        color: "text-sky-400"
                      },
                      {
                        title: "Klik Terakumulasi",
                        value: simulatedStats.clicks.toLocaleString(),
                        unit: "Klik Orang Tua",
                        color: "text-amber-400"
                      },
                      {
                        title: "Click-Through Rate (CTR)",
                        value: simulatedStats.ctr + "%",
                        unit: "Rasio Konversi",
                        color: "text-emerald-400"
                      },
                      {
                        title: "Biaya Berjalan",
                        value: formatIDR(simulatedStats.currentCost),
                        unit: "Berdasarkan Skema",
                        color: "text-pink-400"
                      }
                    ].map((card, idx) => (
                      <div key={idx} className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-5 shadow-xl relative overflow-hidden">
                        <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-red-500 animate-ping" />
                        <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">{card.title}</span>
                        <div className={`text-2xl font-extrabold mt-2 ${card.color}`}>{card.value}</div>
                        <span className="text-[10px] text-slate-400 font-bold mt-1 block">{card.unit}</span>
                      </div>
                    ))}
                  </div>

                  {/* Design visual of active banner & simulation explanation */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* Visual Preview */}
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 lg:col-span-2 space-y-4">
                      <h4 className="font-display font-bold text-sm uppercase tracking-wider text-slate-400">
                        Preview Tampilan Banner Iklan di Pojok Orang Tua
                      </h4>
                      <div className="w-full rounded-[2rem] bg-slate-950 border border-slate-850 p-4 flex flex-col sm:flex-row items-center gap-4 shadow-xl">
                        <div className="w-full sm:w-40 h-24 rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden flex-shrink-0 relative">
                          <img src={campaign.imageUrl} alt={campaign.brandName} className="w-full h-full object-cover" onError={(e) => { e.target.src = "https://placehold.co/300x150/1e293b/cbd5e1?text=No+Preview"; }} />
                          <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-slate-950/80 backdrop-blur-md border border-slate-800/60 flex items-center gap-1">
                            <ShieldCheck className="w-2.5 h-2.5 text-emerald-400" />
                            <span className="text-[8px] font-extrabold tracking-wider text-slate-300 uppercase">Terkurasi</span>
                          </div>
                        </div>
                        <div className="flex-1 self-stretch flex flex-col justify-between py-0.5">
                          <div>
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-[9px] font-extrabold tracking-wider text-slate-400 uppercase">Rekomendasi Partner Orang Tua</span>
                              <div className="w-1 h-1 rounded-full bg-slate-600" />
                              <span className="text-[9px] font-extrabold tracking-wider text-amber-400 uppercase">Sponsor</span>
                            </div>
                            <h5 className="font-display font-bold text-sm text-slate-100">{campaign.brandName}</h5>
                            <p className="text-[10px] text-slate-400 font-bold mt-1 line-clamp-2">
                              Materi promosi terkurasi terintegrasi langsung di viewport Pojok Orang Tua secara natural dan edukatif.
                            </p>
                          </div>
                          <div className="flex items-center gap-1 text-[10px] font-extrabold text-sky-400 mt-2 sm:mt-0">
                            <span>Kunjungi Website Resmi</span>
                            <ExternalLink className="w-3 h-3" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Automation guide info */}
                    <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 flex flex-col justify-between">
                      <div>
                        <h4 className="font-display font-bold text-base mb-2">Bagaimana Sistem Bekerja? 🤖</h4>
                        <p className="text-xs text-slate-400 font-bold leading-relaxed">
                          Sistem penayangan iklan terkurasi BabyLabs bekerja secara otomatis:
                        </p>
                        <div className="space-y-3 mt-4 text-xs text-slate-400 font-bold">
                          <div className="flex gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                            <span>Metrik di atas terus terakumulasi setiap kali orang tua melihat atau mengklik banner Anda.</span>
                          </div>
                          <div className="flex gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                            <span>Sistem secara otomatis menerbitkan tagihan yang disesuaikan secara real-time pada halaman Invoice.</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-3.5 bg-slate-950/40 border border-slate-850 rounded-2xl text-[10px] text-slate-500 font-bold text-center mt-6">
                        Anggaran Maksimal: {formatIDR(campaign.totalBudget)}
                      </div>
                    </div>

                  </div>
                </motion.div>
              )}

              {/* STEP 4: AUTOMATED INVOICE GENERATOR */}
              {activeStep === "invoice" && campaign && (
                <motion.div
                  key="invoice"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  className="max-w-3xl mx-auto space-y-6"
                >
                  {/* Controls bar */}
                  <div className="flex justify-between items-center">
                    <Button
                      onClick={() => setActiveStep("dashboard")}
                      className="h-10 rounded-xl bg-slate-850 border border-slate-700/60 text-slate-300 font-bold text-xs"
                    >
                      Kembali ke Dashboard Laporan
                    </Button>

                    <div className="flex gap-2">
                      <button
                        onClick={() => window.print()}
                        className="p-2.5 rounded-xl bg-slate-900 border border-slate-855 hover:bg-slate-800 text-slate-300 transition-all"
                        title="Cetak Invoice"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Invoice Layout (High Fidelity PDF Design) */}
                  <div className="bg-white text-slate-900 rounded-[2rem] p-8 sm:p-10 shadow-2xl relative border-4 border-slate-200">
                    <div className="absolute top-10 right-10 flex flex-col items-end">
                      <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Status Invoice</span>
                      <div className="mt-1 px-4 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-600 text-xs font-extrabold uppercase tracking-widest">
                        PENDING PAYMENT
                      </div>
                    </div>

                    {/* Company Header */}
                    <div className="pb-8 border-b border-slate-100">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-slate-950 flex items-center justify-center text-white font-extrabold text-sm">
                          B
                        </div>
                        <span className="font-display font-extrabold text-base tracking-wide text-slate-900">BabyLabs</span>
                      </div>
                      <p className="text-[10px] text-slate-500 font-bold leading-relaxed max-w-xs">
                        PT Inovasi Stimulasi Anak Bangsa<br />
                        Gedung Cyber 2 Lantai 18, Jakarta Selatan<br />
                        support@babylabs.co.id
                      </p>
                    </div>

                    {/* Invoice Metadata */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 py-8 border-b border-slate-100 text-xs">
                      <div>
                        <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">Pengiklan / Brand</span>
                        <div className="font-bold text-slate-850 mt-1">{campaign.brandName}</div>
                      </div>
                      <div>
                        <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">Nomor Invoice</span>
                        <div className="font-bold text-slate-850 mt-1 font-mono">{campaign.invoiceNo}</div>
                      </div>
                      <div>
                        <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">Tanggal Invoice</span>
                        <div className="font-bold text-slate-850 mt-1 font-mono">
                          {new Date().toLocaleDateString("id-ID")}
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">Jatuh Tempo</span>
                        <div className="font-bold text-slate-850 mt-1 font-mono">
                          {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString("id-ID")}
                        </div>
                      </div>
                    </div>

                    {/* Invoice Items table */}
                    <div className="py-8">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead>
                          <tr className="border-b border-slate-200 text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">
                            <th className="pb-3">Deskripsi Kampanye</th>
                            <th className="pb-3 text-center">Skema</th>
                            <th className="pb-3 text-right">Metrik Berjalan</th>
                            <th className="pb-3 text-right">Total Tagihan</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-bold text-slate-800">
                          <tr>
                            <td className="py-4">
                              <div className="font-extrabold text-slate-900">{campaign.brandName} Banner Ad Placement</div>
                              <div className="text-[10px] text-slate-500 font-bold mt-1">
                                Periode: {new Date(campaign.startDate).toLocaleDateString("id-ID")} - {new Date(campaign.endDate).toLocaleDateString("id-ID")}<br />
                                Penempatan: Pojok Orang Tua - Header Banner
                              </div>
                            </td>
                            <td className="py-4 text-center">
                              {campaign.packageType === "fixed" && "Fixed Rate"}
                              {campaign.packageType === "cpm" && "Rp 50.000 / 1.000 Impresi"}
                              {campaign.packageType === "cpc" && "Rp 2.000 / Klik"}
                            </td>
                            <td className="py-4 text-right font-mono">
                              {campaign.packageType === "fixed" && "1 Bulan"}
                              {campaign.packageType === "cpm" && `${simulatedStats.impressions.toLocaleString()} Impresi`}
                              {campaign.packageType === "cpc" && `${simulatedStats.clicks.toLocaleString()} Klik`}
                            </td>
                            <td className="py-4 text-right font-mono text-slate-900">
                              {formatIDR(simulatedStats.currentCost)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Cost totals */}
                    <div className="border-t border-slate-100 pt-6 flex flex-col items-end text-xs">
                      <div className="w-72 space-y-3 font-bold text-slate-700">
                        <div className="flex justify-between">
                          <span>Subtotal:</span>
                          <span className="font-mono text-slate-900">{formatIDR(simulatedStats.currentCost)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>PPN (11%):</span>
                          <span className="font-mono text-slate-900">{formatIDR(Math.round(simulatedStats.currentCost * 0.11))}</span>
                        </div>
                        <div className="flex justify-between text-base font-extrabold text-slate-900 pt-3 border-t border-slate-100">
                          <span>Total Pembayaran:</span>
                          <span className="font-mono text-amber-600">{formatIDR(Math.round(simulatedStats.currentCost * 1.11))}</span>
                        </div>
                      </div>
                    </div>

                    {/* Footer transfer instruction */}
                    <div className="mt-12 pt-8 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-6 text-[10px] text-slate-500 font-bold">
                      <div>
                        <h5 className="text-slate-800 font-extrabold uppercase tracking-wider mb-2">Instruksi Pembayaran Bank Transfer</h5>
                        <p className="leading-relaxed">
                          Bank Central Asia (BCA)<br />
                          No. Rekening Virtual Account: <span className="font-mono font-bold text-slate-800 text-xs">809396-0494-0998</span><br />
                          Atas Nama: PT Inovasi Stimulasi Anak Bangsa
                        </p>
                      </div>
                      <div className="flex flex-col justify-end">
                        <p className="leading-relaxed text-right sm:text-left">
                          * Sistem pencatatan invoice ini terintegrasi langsung dengan database pelacakan analitik backend BabyLabs.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        ) : (
          /* TAB 2: MY CAMPAIGNS (REAL DATABASE DASHBOARD) */
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-100">Dasbor Kampanye Iklan Riil 📊</h2>
                <p className="text-xs text-slate-400 font-bold mt-1">Kelola moderasi, pelacakan metrik, dan invoice pembayaran kampanye aktif Anda.</p>
              </div>
              <Button
                onClick={fetchRealCampaigns}
                className="h-10 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 text-xs font-bold flex items-center gap-1.5 hover:bg-slate-800"
                disabled={fetchingCampaigns}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${fetchingCampaigns ? 'animate-spin' : ''}`} />
                <span>Segarkan</span>
              </Button>
            </div>

            {realCampaigns.length === 0 ? (
              <div className="bg-slate-900/40 border border-slate-800/80 rounded-3xl p-12 text-center max-w-xl mx-auto space-y-4 shadow-xl">
                <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-center justify-center text-amber-400 mx-auto">
                  <Calculator className="w-6 h-6" />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-200">Belum Ada Kampanye Iklan</h3>
                <p className="text-xs text-slate-400 font-bold leading-normal">Anda belum mendaftarkan kampanye iklan riil. Silakan buat kampanye baru melalui kalkulator tarif.</p>
                <Button
                  onClick={() => setActiveTab("create")}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs h-10 rounded-xl px-5"
                >
                  Buat Iklan Sekarang
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                
                {/* Left side: Campaigns list */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-4 shadow-xl space-y-3">
                    <h3 className="font-display font-bold text-sm text-slate-300 px-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-amber-400" />
                      Daftar Kampanye
                    </h3>
                    <div className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
                      {realCampaigns.map((ad) => {
                        const isSelected = selectedRealCampaignId === ad.id;
                        
                        // Status styling
                        const isApproved = ad.moderation_status === "approved" && ad.payment_status === "paid";
                        const isPendingReview = ad.moderation_status === "pending_review" && ad.payment_status === "paid";
                        const isPendingPayment = ad.payment_status === "pending_payment";
                        const isRejected = ad.moderation_status === "rejected";

                        return (
                          <button
                            key={ad.id}
                            type="button"
                            onClick={() => setSelectedRealCampaignId(ad.id)}
                            className={`w-full text-left p-3.5 rounded-2xl border transition-all space-y-3 relative overflow-hidden ${
                              isSelected
                                ? "bg-slate-850 border-amber-500 shadow-md"
                                : "bg-slate-950/60 border-slate-850 hover:border-slate-800"
                            }`}
                          >
                            <div className="flex justify-between items-start gap-2">
                              <div>
                                <h4 className="font-bold text-xs text-slate-200 line-clamp-1">{ad.advertiser_name}</h4>
                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{getPackageLabel(ad.package_type)}</span>
                              </div>

                              {/* Status Badge */}
                              <div className="flex flex-col items-end gap-1">
                                {isApproved && (
                                  <span className="text-[8px] bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                    Aktif
                                  </span>
                                )}
                                {isPendingReview && (
                                  <span className="text-[8px] bg-sky-500/10 border border-sky-500/30 text-sky-400 font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider flex items-center gap-1">
                                    <Clock className="w-2.5 h-2.5" />
                                    Ulasan
                                  </span>
                                )}
                                {isPendingPayment && (
                                  <span className="text-[8px] bg-amber-500/10 border border-amber-500/30 text-amber-400 font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider flex items-center gap-1">
                                    <CreditCard className="w-2.5 h-2.5" />
                                    Belum Bayar
                                  </span>
                                )}
                                {isRejected && (
                                  <span className="text-[8px] bg-rose-500/10 border border-rose-500/30 text-rose-400 font-extrabold px-1.5 py-0.5 rounded uppercase tracking-wider flex items-center gap-1">
                                    Ditolak
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex justify-between text-[10px] text-slate-500 font-bold pt-2 border-t border-slate-900">
                              <span>Metrik:</span>
                              <span className="font-mono text-slate-300">
                                {ad.stats?.impressions || 0} Imp | {ad.stats?.clicks || 0} Klik
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right side: Selected campaign details & metrics */}
                <div className="lg:col-span-2 space-y-6">
                  {selectedRealCampaign ? (
                    <div className="space-y-6">
                      
                      {/* Campaign header details */}
                      <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 shadow-xl space-y-4">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                          <div>
                            <h3 className="font-display font-bold text-xl text-slate-100">{selectedRealCampaign.advertiser_name}</h3>
                            <p className="text-xs text-slate-500 font-mono font-bold mt-1">ID: {selectedRealCampaign.id}</p>
                          </div>
                          
                          {/* Main Actions */}
                          <div className="flex gap-2.5 w-full sm:w-auto">
                            {selectedRealCampaign.payment_status === "pending_payment" && (
                              <Button
                                onClick={() => triggerMidtransPayment(selectedRealCampaign.midtrans_token, `/simulated-checkout?token=${selectedRealCampaign.midtrans_token}`, selectedRealCampaign.id)}
                                className="flex-1 sm:flex-initial h-10 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold flex items-center justify-center gap-1.5 shadow-md shadow-amber-500/10"
                              >
                                <CreditCard className="w-4 h-4" />
                                <span>Bayar Sekarang</span>
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Status descriptions */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                          <div className="p-3 bg-slate-950/40 border border-slate-850 rounded-2xl">
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Status Pembayaran</span>
                            <span className={`text-xs font-extrabold uppercase tracking-wider ${selectedRealCampaign.payment_status === 'paid' ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {selectedRealCampaign.payment_status === 'paid' ? 'LUNAS (PAID)' : 'MENUNGGU PEMBAYARAN'}
                            </span>
                          </div>
                          
                          <div className="p-3 bg-slate-950/40 border border-slate-850 rounded-2xl">
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Status Moderasi Admin</span>
                            <span className={`text-xs font-extrabold uppercase tracking-wider ${
                              selectedRealCampaign.moderation_status === 'approved' ? 'text-emerald-400' : 
                              selectedRealCampaign.moderation_status === 'pending_review' ? 'text-sky-400' : 'text-rose-400'
                            }`}>
                              {selectedRealCampaign.moderation_status === 'approved' && 'DISETUJUI & TAYANG'}
                              {selectedRealCampaign.moderation_status === 'pending_review' && 'MENUNGGU AUDIT KONTEN'}
                              {selectedRealCampaign.moderation_status === 'rejected' && 'DITOLAK ADMIN'}
                            </span>
                          </div>
                        </div>

                        {selectedRealCampaign.moderation_status === "rejected" && (
                          <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-2xl text-xs text-rose-300 font-bold flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-rose-400 mt-0.5" />
                            <div>
                              <span><strong>Alasan Penolakan Admin:</strong></span>
                              <p className="mt-1 text-rose-400/90 leading-relaxed font-sans">{selectedRealCampaign.rejection_reason || "Konten tidak ramah anak atau melanggar hak cipta."}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Real metrics counter cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-5 shadow-xl">
                          <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Total Impresi</span>
                          <div className="text-2xl font-extrabold mt-1.5 text-sky-400">{(selectedRealCampaign.stats?.impressions || 0).toLocaleString()}</div>
                          <span className="text-[9px] text-slate-400 font-bold mt-1 block">Telah Tayang</span>
                        </div>
                        <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-5 shadow-xl">
                          <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Total Klik</span>
                          <div className="text-2xl font-extrabold mt-1.5 text-amber-400">{(selectedRealCampaign.stats?.clicks || 0).toLocaleString()}</div>
                          <span className="text-[9px] text-slate-400 font-bold mt-1 block">Konversi Website</span>
                        </div>
                        <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-5 shadow-xl">
                          <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Click-Through Rate (CTR)</span>
                          <div className="text-2xl font-extrabold mt-1.5 text-emerald-400">{(selectedRealCampaign.stats?.ctr || 0.0).toFixed(2)}%</div>
                          <span className="text-[9px] text-slate-400 font-bold mt-1 block">Efektivitas Iklan</span>
                        </div>
                      </div>

                      {/* Preview banner card */}
                      <div className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 space-y-4 shadow-xl">
                        <h4 className="font-display font-bold text-sm uppercase tracking-wider text-slate-400">
                          Pratinjau Banner Iklan
                        </h4>
                        <div className="w-full rounded-[2.2rem] bg-slate-950 border border-slate-850 p-4 flex flex-col sm:flex-row items-center gap-4">
                          <div className="w-full sm:w-40 h-24 rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden flex-shrink-0 relative">
                            <img src={selectedRealCampaign.image_url} alt={selectedRealCampaign.advertiser_name} className="w-full h-full object-cover" onError={(e) => { e.target.src = "https://placehold.co/300x150/1e293b/cbd5e1?text=No+Preview"; }} />
                            <div className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-slate-950/80 border border-slate-800/60 flex items-center gap-1">
                              <ShieldCheck className="w-2.5 h-2.5 text-emerald-400" />
                              <span className="text-[8px] font-extrabold tracking-wider text-slate-300 uppercase">Terkurasi</span>
                            </div>
                          </div>
                          <div className="flex-1 self-stretch flex flex-col justify-between py-0.5">
                            <div>
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="text-[9px] font-extrabold tracking-wider text-slate-400 uppercase">Rekomendasi Partner Orang Tua</span>
                                <div className="w-1 h-1 rounded-full bg-slate-600" />
                                <span className="text-[9px] font-extrabold tracking-wider text-amber-400 uppercase">Sponsor</span>
                              </div>
                              <h5 className="font-display font-bold text-sm text-slate-100">{selectedRealCampaign.advertiser_name}</h5>
                              <p className="text-[10px] text-slate-400 font-bold mt-1 line-clamp-2">
                                Penempatan: Pojok Orang Tua - Header Banner. Menjangkau ribuan profil keluarga premium secara berpenargetan.
                              </p>
                            </div>
                            <div className="flex items-center gap-1 text-[10px] font-extrabold text-sky-400 mt-2 sm:mt-0">
                              <a href={selectedRealCampaign.target_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:underline">
                                <span>Tautan Tujuan</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Display Invoice for Real campaign */}
                      <div className="bg-white text-slate-900 rounded-[2rem] p-8 sm:p-10 shadow-2xl relative border-4 border-slate-200">
                        <div className="absolute top-10 right-10 flex flex-col items-end">
                          <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wider">Status Invoice</span>
                          <div className={`mt-1 px-4 py-1.5 rounded-full border text-xs font-extrabold uppercase tracking-widest ${
                            selectedRealCampaign.payment_status === 'paid'
                              ? 'bg-emerald-100 border-emerald-300 text-emerald-600'
                              : 'bg-amber-100 border-amber-300 text-amber-600'
                          }`}>
                            {selectedRealCampaign.payment_status === 'paid' ? 'PAID / LUNAS' : 'UNPAID'}
                          </div>
                        </div>

                        {/* Company Header */}
                        <div className="pb-8 border-b border-slate-100">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg bg-slate-950 flex items-center justify-center text-white font-extrabold text-sm">
                              B
                            </div>
                            <span className="font-display font-extrabold text-base tracking-wide text-slate-900">BabyLabs</span>
                          </div>
                          <p className="text-[10px] text-slate-500 font-bold leading-relaxed max-w-xs">
                            PT Inovasi Stimulasi Anak Bangsa<br />
                            Gedung Cyber 2 Lantai 18, Jakarta Selatan<br />
                            support@babylabs.co.id
                          </p>
                        </div>

                        {/* Invoice Metadata */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 py-8 border-b border-slate-100 text-xs">
                          <div>
                            <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">Pengiklan / Brand</span>
                            <div className="font-bold text-slate-850 mt-1">{selectedRealCampaign.advertiser_name}</div>
                          </div>
                          <div>
                            <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">Nomor Invoice</span>
                            <div className="font-bold text-slate-850 mt-1 font-mono">INV-{selectedRealCampaign.id.substring(0, 8).toUpperCase()}</div>
                          </div>
                          <div>
                            <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">Tanggal Mulai</span>
                            <div className="font-bold text-slate-850 mt-1">
                              {new Date(selectedRealCampaign.start_date).toLocaleDateString("id-ID")}
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">Tanggal Selesai</span>
                            <div className="font-bold text-slate-850 mt-1">
                              {new Date(selectedRealCampaign.end_date).toLocaleDateString("id-ID")}
                            </div>
                          </div>
                        </div>

                        {/* Cost calculation */}
                        <div className="py-8">
                          <table className="w-full text-left border-collapse text-xs">
                            <thead>
                              <tr className="border-b border-slate-200 text-slate-400 font-extrabold uppercase tracking-wider text-[9px]">
                                <th className="pb-3">Deskripsi Kampanye</th>
                                <th className="pb-3 text-center">Skema</th>
                                <th className="pb-3 text-right">Metrik Target</th>
                                <th className="pb-3 text-right">Anggaran</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-bold text-slate-800">
                              <tr>
                                <td className="py-4">
                                  <div className="font-extrabold text-slate-900">{selectedRealCampaign.advertiser_name} Banner Ad Placement</div>
                                </td>
                                <td className="py-4 text-center">
                                  {selectedRealCampaign.package_type === "fixed" && "Fixed Rate"}
                                  {selectedRealCampaign.package_type === "cpm" && "Rp 50.000 / 1.000 Impresi"}
                                  {selectedRealCampaign.package_type === "cpc" && "Rp 2.000 / Klik"}
                                </td>
                                <td className="py-4 text-right font-mono">
                                  {selectedRealCampaign.package_type === "fixed" && "1 Bulan"}
                                  {selectedRealCampaign.package_type === "cpm" && `${(selectedRealCampaign.target_limit || 0).toLocaleString()} Impresi`}
                                  {selectedRealCampaign.package_type === "cpc" && `${(selectedRealCampaign.target_limit || 0).toLocaleString()} Klik`}
                                </td>
                                <td className="py-4 text-right font-mono text-slate-900">
                                  {selectedRealCampaign.package_type === "fixed" && formatIDR(2500000)}
                                  {selectedRealCampaign.package_type === "cpm" && formatIDR(((selectedRealCampaign.target_limit || 0) / 1000) * 50000)}
                                  {selectedRealCampaign.package_type === "cpc" && formatIDR((selectedRealCampaign.target_limit || 0) * 2000)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Totals */}
                        <div className="border-t border-slate-100 pt-6 flex flex-col items-end text-xs font-bold text-slate-700">
                          <div className="w-72 space-y-3">
                            <div className="flex justify-between">
                              <span>Total Budget:</span>
                              <span className="font-mono text-slate-900">
                                {selectedRealCampaign.package_type === "fixed" && formatIDR(2500000)}
                                {selectedRealCampaign.package_type === "cpm" && formatIDR(((selectedRealCampaign.target_limit || 0) / 1000) * 50000)}
                                {selectedRealCampaign.package_type === "cpc" && formatIDR((selectedRealCampaign.target_limit || 0) * 2000)}
                              </span>
                            </div>
                            <div className="flex justify-between text-base font-extrabold text-slate-900 pt-3 border-t border-slate-100">
                              <span>Total Pembayaran:</span>
                              <span className="font-mono text-amber-600">
                                {selectedRealCampaign.package_type === "fixed" && formatIDR(Math.round(2500000 * 1.11))}
                                {selectedRealCampaign.package_type === "cpm" && formatIDR(Math.round(((selectedRealCampaign.target_limit || 0) / 1000) * 50000 * 1.11))}
                                {selectedRealCampaign.package_type === "cpc" && formatIDR(Math.round((selectedRealCampaign.target_limit || 0) * 2000 * 1.11))}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  ) : (
                    <div className="bg-slate-900/40 border border-slate-800/80 rounded-3xl p-12 text-center shadow-xl">
                      <p className="text-slate-400 font-bold text-sm">Silakan pilih salah satu kampanye di daftar sebelah kiri untuk melihat rincian.</p>
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        )}

      </div>

      {/* AUTHENTICATION MODAL (LOGIN & REGISTRATION) */}
      <AnimatePresence>
        {showAuthModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-[2rem] p-6 sm:p-8 shadow-2xl relative overflow-hidden text-slate-200"
            >
              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-28 h-28 bg-amber-500/10 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-10 -left-10 w-28 h-28 bg-sky-500/10 rounded-full blur-2xl pointer-events-none" />

              {/* Close Button */}
              <button
                onClick={() => setShowAuthModal(false)}
                className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 text-sm font-bold p-2"
              >
                ✕
              </button>

              <h2 className="font-display text-2xl font-bold text-center bg-gradient-to-r from-amber-400 to-amber-200 bg-clip-text text-transparent mb-1">
                {authMode === "login" ? "Masuk Pengiklan" : "Daftar Akun Pengiklan"}
              </h2>
              <p className="text-xs text-slate-400 text-center mb-6 font-bold">
                {authMode === "login" ? "Akses dasbor dan lakukan monitoring performa iklan." : "Mulai kemitraan mandiri berpenargetan premium."}
              </p>

              {authMode === "login" ? (
                /* LOGIN FORM */
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <Mail className="w-4 h-4" />
                    </div>
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="Alamat Email"
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-slate-950/50 border border-slate-800 focus:outline-none focus:border-amber-400 font-bold text-xs text-slate-200 placeholder-slate-500"
                      required
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-slate-950/50 border border-slate-800 focus:outline-none focus:border-amber-400 font-bold text-xs text-slate-200 placeholder-slate-500"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-bold text-xs shadow-md shadow-amber-500/10 mt-2"
                    disabled={loading}
                  >
                    {loading ? "Menghubungkan..." : "Masuk"}
                  </Button>

                  <p className="text-center text-[10px] text-slate-400 font-bold mt-4">
                    Belum memiliki akun?{" "}
                    <button
                      type="button"
                      onClick={() => setAuthMode("register")}
                      className="text-amber-400 hover:underline font-extrabold"
                    >
                      Daftar Baru
                    </button>
                  </p>
                </form>
              ) : (
                /* REGISTER FORM */
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <UserIcon className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      placeholder="Nama Lengkap / Perusahaan"
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-slate-950/50 border border-slate-800 focus:outline-none focus:border-amber-400 font-bold text-xs text-slate-200 placeholder-slate-500"
                      required
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <Mail className="w-4 h-4" />
                    </div>
                    <input
                      type="email"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      placeholder="Alamat Email"
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-slate-950/50 border border-slate-800 focus:outline-none focus:border-amber-400 font-bold text-xs text-slate-200 placeholder-slate-500"
                      required
                    />
                  </div>

                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <Lock className="w-4 h-4" />
                    </div>
                    <input
                      type="password"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      placeholder="Buat Password"
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-slate-950/50 border border-slate-800 focus:outline-none focus:border-amber-400 font-bold text-xs text-slate-200 placeholder-slate-500"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-bold text-xs shadow-md shadow-amber-500/10 mt-2"
                    disabled={loading}
                  >
                    {loading ? "Mendaftarkan..." : "Daftar Akun"}
                  </Button>

                  <p className="text-center text-[10px] text-slate-400 font-bold mt-4">
                    Sudah memiliki akun?{" "}
                    <button
                      type="button"
                      onClick={() => setAuthMode("login")}
                      className="text-amber-400 hover:underline font-extrabold"
                    >
                      Masuk Di Sini
                    </button>
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
